import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useLiveRefresh } from '../hooks/useLiveRefresh';
import { apiCall } from '../utils/api';
import { requestNotificationPermission, getNotificationPermission } from '../utils/pushNotifications';
import {
  Bell, X, Briefcase, DollarSign, MessageSquare, CheckCircle, Users,
  Settings, Volume2, VolumeX, BellRing, Clock, AlertTriangle, Award,
  RefreshCw, FileText, AlertOctagon, Activity, Repeat
} from 'lucide-react';

// Maps backend notification `type` → display config (icon + bg + title fallback).
// Server-supplied `title` and `message` always win; this is just the fallback +
// visual treatment so every event type reads as a first-class notification.
const TYPE_CONFIG = {
  job_posted:                  { icon: Briefcase,    color: 'blue',     fallbackTitle: 'New Job Available' },
  job_picked:                  { icon: Users,        color: 'emerald',  fallbackTitle: '🎉 Shift Filled!' },
  shift_filled:                { icon: Users,        color: 'emerald',  fallbackTitle: '🎉 Shift Filled!' },
  new_application:             { icon: Briefcase,    color: 'blue',     fallbackTitle: '📋 New Application' },
  shift_cancelled:             { icon: AlertTriangle,color: 'amber',    fallbackTitle: '⚠️ Shift Cancelled' },
  shift_postponed:             { icon: Clock,        color: 'amber',    fallbackTitle: 'Shift Postponed' },
  shift_unavailable:           { icon: AlertTriangle,color: 'amber',    fallbackTitle: 'Shift Unavailable' },
  shift_reminder:              { icon: Clock,        color: 'indigo',   fallbackTitle: '⏰ Shift Starting Soon' },
  job_confirmed:               { icon: CheckCircle,  color: 'indigo',   fallbackTitle: 'Shift Confirmed' },
  shift_confirmed:             { icon: CheckCircle,  color: 'emerald',  fallbackTitle: 'Shift Confirmed' },
  shift_approved:              { icon: CheckCircle,  color: 'emerald',  fallbackTitle: 'Shift Approved' },
  shift_rejected:              { icon: AlertOctagon, color: 'red',      fallbackTitle: 'Shift Rejected' },
  shift_request:               { icon: Briefcase,    color: 'blue',     fallbackTitle: 'New Shift Request' },
  shift_auto_reopened:         { icon: RefreshCw,    color: 'amber',    fallbackTitle: 'Shift Re-opened' },
  payment_received:            { icon: DollarSign,   color: 'green',    fallbackTitle: '💰 Payment Received' },
  payment_approved:            { icon: DollarSign,   color: 'green',    fallbackTitle: '💰 Payment Approved' },
  new_message:                 { icon: MessageSquare,color: 'purple',   fallbackTitle: '💬 New Message' },
  broadcast:                   { icon: MessageSquare,color: 'purple',   fallbackTitle: 'Broadcast' },
  worker_checked_in:           { icon: Activity,     color: 'emerald',  fallbackTitle: '🟢 Worker Clocked In' },
  worker_checked_out:          { icon: Clock,        color: 'slate',    fallbackTitle: '🔵 Worker Clocked Out' },
  tasks_all_complete:          { icon: CheckCircle,  color: 'emerald',  fallbackTitle: '✅ Tasks Complete' },
  timesheet_acknowledged:      { icon: FileText,     color: 'blue',     fallbackTitle: 'Timesheet Acknowledged' },
  timesheet_edited:            { icon: FileText,     color: 'amber',    fallbackTitle: 'Timesheet Updated' },
  timesheet_dispute:           { icon: AlertTriangle,color: 'amber',    fallbackTitle: '⚠️ Timesheet Disputed' },
  dispute_resolved:            { icon: CheckCircle,  color: 'indigo',   fallbackTitle: 'Dispute Resolved' },
  cert_pending_review:         { icon: Award,        color: 'blue',     fallbackTitle: 'Certificate Awaiting Review' },
  cert_verified:               { icon: Award,        color: 'emerald',  fallbackTitle: '✅ Certificate Verified' },
  cert_rejected:               { icon: Award,        color: 'red',      fallbackTitle: '❌ Certificate Rejected' },
  shift_swap_posted:           { icon: Repeat,       color: 'blue',     fallbackTitle: 'Shift Swap Requested' },
  shift_swap_claimed:          { icon: Repeat,       color: 'indigo',   fallbackTitle: 'Swap Claimed' },
  shift_swap_claimed_yours:    { icon: Repeat,       color: 'emerald',  fallbackTitle: '🎉 Your Shift Was Picked Up' },
  shift_transfer_request:      { icon: Repeat,       color: 'blue',     fallbackTitle: 'Transfer Request' },
  shift_transfer_requested:    { icon: Repeat,       color: 'blue',     fallbackTitle: 'Transfer Pending Approval' },
  transfer_accepted:           { icon: CheckCircle,  color: 'emerald',  fallbackTitle: 'Transfer Accepted' },
  transfer_rejected:           { icon: AlertOctagon, color: 'amber',    fallbackTitle: 'Transfer Declined' },
  transfer_completed:          { icon: CheckCircle,  color: 'emerald',  fallbackTitle: 'Transfer Completed' },
  time_edit_request:           { icon: Clock,        color: 'blue',     fallbackTitle: 'Time Edit Request' },
  time_edit_response:          { icon: Clock,        color: 'indigo',   fallbackTitle: 'Time Edit Update' },
  training_completed:          { icon: Award,        color: 'emerald',  fallbackTitle: '🎓 Training Quiz Completed' },
  emergency_sos:               { icon: AlertOctagon, color: 'red',      fallbackTitle: '🚨 EMERGENCY SOS' },
  incident_report:             { icon: AlertOctagon, color: 'red',      fallbackTitle: '🚨 Incident Report' },
  direct_message:              { icon: MessageSquare,color: 'purple',   fallbackTitle: '💬 New Message' },
  application_approved:        { icon: CheckCircle,  color: 'emerald',  fallbackTitle: '✅ Application Approved' },
  training_reminder:           { icon: Award,        color: 'amber',    fallbackTitle: '🎓 Training Reminder' },
  marketplace_listing_approve: { icon: Briefcase,    color: 'emerald',  fallbackTitle: 'Listing Approved' },
  marketplace_listing_reject:  { icon: AlertOctagon, color: 'amber',    fallbackTitle: 'Listing Rejected' },
  marketplace_listing_archive: { icon: Briefcase,    color: 'slate',    fallbackTitle: 'Listing Archived' },
  marketplace_featured:        { icon: Award,        color: 'amber',    fallbackTitle: '⭐ Listing Featured' },
  confirm_reminder:            { icon: Clock,        color: 'amber',    fallbackTitle: 'Please Confirm Your Shift' },
  hris_expired:                { icon: AlertOctagon, color: 'red',      fallbackTitle: '❌ Credential Expired' },
  hris_expiring_7d:            { icon: AlertTriangle,color: 'amber',    fallbackTitle: '⚠️ Credential Expires in 7 Days' },
  hris_expiring_30d:           { icon: Clock,        color: 'indigo',   fallbackTitle: 'Credential Expires in 30 Days' },
  support_mode_expiring_15m:   { icon: AlertTriangle,color: 'orange',   fallbackTitle: 'Admin Support Session Ending Soon' },
  training_assigned:           { icon: FileText,     color: 'indigo',   fallbackTitle: '🎓 New Training Assigned' },
  training_due_soon:           { icon: Clock,        color: 'amber',    fallbackTitle: '⏰ Training Due Soon' },
  training_certificate:        { icon: Award,        color: 'emerald',  fallbackTitle: '🎓 Certificate Issued' },
  training_overdue_escalation: { icon: AlertOctagon, color: 'red',      fallbackTitle: '🚨 Training Overdue — Action Needed' },
  training_nudge:              { icon: BellRing,     color: 'indigo',   fallbackTitle: '👋 Reminder from your manager' },
  hr_weekly_digest:            { icon: FileText,     color: 'indigo',   fallbackTitle: '📋 Weekly HR digest' },
};

const NudgeButton = React.lazy(() => import('./hris/HRISTrainingEscalationCard').then(m => ({ default: m.NudgeButton })));

const FILTER_OUT_TYPES = new Set(['pong', 'message_sent', 'typing', 'stop_typing']);

const colorClasses = {
  emerald: { icon: 'text-emerald-600', bg: 'bg-emerald-100', border: 'border-l-emerald-500', tint: 'bg-emerald-50' },
  red:     { icon: 'text-red-600',     bg: 'bg-red-100',     border: 'border-l-red-500',     tint: 'bg-red-50' },
  amber:   { icon: 'text-amber-600',   bg: 'bg-amber-100',   border: 'border-l-amber-500',   tint: 'bg-amber-50' },
  blue:    { icon: 'text-blue-600',    bg: 'bg-blue-100',    border: 'border-l-blue-500',    tint: 'bg-blue-50' },
  indigo:  { icon: 'text-indigo-600',  bg: 'bg-indigo-100',  border: 'border-l-indigo-500',  tint: 'bg-indigo-50' },
  purple:  { icon: 'text-purple-600',  bg: 'bg-purple-100',  border: 'border-l-purple-500',  tint: 'bg-purple-50' },
  green:   { icon: 'text-green-600',   bg: 'bg-green-100',   border: 'border-l-green-500',   tint: 'bg-green-50' },
  slate:   { icon: 'text-slate-600',   bg: 'bg-slate-100',   border: 'border-l-slate-400',   tint: 'bg-slate-50' },
};

function getConfig(type) {
  return TYPE_CONFIG[type] || { icon: Bell, color: 'slate', fallbackTitle: 'Notification' };
}

function formatTimeAgo(timestamp) {
  if (!timestamp) return '';
  const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  if (seconds < 0) return 'Just now';
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export default function NotificationBell({ onNavigate } = {}) {
  const {
    isConnected,
    lastMessage,
    notificationSettings,
    updateNotificationSettings,
  } = useWebSocket();

  // Single source of truth: an array of notification objects from backend, augmented with
  // any incoming WebSocket messages in real time. Each item has {_id?, type, title, message,
  // data, read, priority, created_at}.
  const [items, setItems] = useState([]);
  const [personal, setPersonal] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [loading, setLoading] = useState(false);
  const seenWsTimestampsRef = useRef(new Set());

  // Visible notifications (filter ephemeral types).
  const visibleItems = useMemo(() =>
    items.filter(n => !FILTER_OUT_TYPES.has(n.type)),
  [items]);

  const unreadCount = useMemo(
    () => visibleItems.filter(n => !n.read).length,
    [visibleItems]
  );

  // ─── Initial fetch ────────────────────────────────────────────────────────
  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall('/api/notifications?limit=50');
      const list = Array.isArray(res?.notifications) ? res.notifications : [];
      setPersonal(!!res?.personal);
      // Newest first (server already sorts desc, but be defensive)
      list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      setItems(list);
    } catch {
      // Silently — bell still works with WS-only events
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Live sync: badge + list refresh when notifications change server-side.
  useLiveRefresh(['notifications'], fetchHistory, { debounceMs: 500 });

  // ─── Real-time WS append ──────────────────────────────────────────────────
  useEffect(() => {
    if (!lastMessage || !lastMessage.type) return;
    if (FILTER_OUT_TYPES.has(lastMessage.type)) return;

    // De-dupe within session: skip if we've already added this exact (type+timestamp).
    const key = `${lastMessage.type}|${lastMessage.timestamp || ''}`;
    if (seenWsTimestampsRef.current.has(key)) return;
    seenWsTimestampsRef.current.add(key);

    setItems(prev => {
      // If a server-side notification with the same type+message already exists in the
      // top 3 (i.e. server inserted + WS pushed), don't duplicate.
      const head = prev.slice(0, 3);
      const isDup = head.some(p =>
        p.type === lastMessage.type &&
        (p.message || '') === (lastMessage.message || '') &&
        Math.abs(new Date(p.created_at || 0).getTime() - Date.now()) < 3000
      );
      if (isDup) return prev;
      const wsItem = {
        _id: `ws-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type: lastMessage.type,
        title: lastMessage.title || getConfig(lastMessage.type).fallbackTitle,
        message: lastMessage.message || lastMessage.data?.message || '',
        data: lastMessage.data || {},
        priority: lastMessage.priority || 'normal',
        read: false,
        created_at: lastMessage.timestamp || new Date().toISOString(),
        _live: true, // marker for "just received" pulse
      };
      return [wsItem, ...prev].slice(0, 80);
    });

    // Trigger background re-fetch so we eventually pull the canonical _id from server.
    // Slight delay so the DB write definitely lands first.
    const t = setTimeout(() => { fetchHistory(); }, 1500);
    return () => clearTimeout(t);
  }, [lastMessage, fetchHistory]);

  // Animate bell when unread changes upward
  const prevUnreadRef = useRef(0);
  useEffect(() => {
    if (unreadCount > prevUnreadRef.current) {
      setAnimate(true);
      const t = setTimeout(() => setAnimate(false), 1000);
      prevUnreadRef.current = unreadCount;
      return () => clearTimeout(t);
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);

  // ─── Actions ──────────────────────────────────────────────────────────────
  const markAllRead = useCallback(async () => {
    setItems(prev => prev.map(n => ({ ...n, read: true })));
    try { await apiCall('/api/notifications/read-all', { method: 'PUT' }); } catch {}
  }, []);

  const markOneRead = useCallback(async (notif) => {
    if (notif.read) return;
    setItems(prev => prev.map(n => (n === notif ? { ...n, read: true } : n)));
    if (notif._id && !String(notif._id).startsWith('ws-')) {
      try { await apiCall(`/api/notifications/${notif._id}/read`, { method: 'PUT' }); } catch {}
    }
  }, []);

  // Navigate to the relevant tab/info when a notification row is clicked.
  // Hosts wire `onNavigate` to receive (type, data, notification) and route
  // to their own tab id. We close the panel after a successful navigation.
  const handleItemClick = useCallback((notif) => {
    markOneRead(notif);
    if (typeof onNavigate === 'function') {
      try { onNavigate(notif.type, notif.data || {}, notif); } catch {}
    }
    setShowPanel(false);
  }, [markOneRead, onNavigate]);

  const handleRequestPushPermission = async () => {
    const result = await requestNotificationPermission();
    updateNotificationSettings({ pushEnabled: result === 'granted' });
  };

  // Mark all as read when panel opens (after a 1.5s window so the user sees the badge first)
  useEffect(() => {
    if (!showPanel || unreadCount === 0) return;
    const t = setTimeout(() => { markAllRead(); }, 1500);
    return () => clearTimeout(t);
  }, [showPanel, unreadCount, markAllRead]);

  return (
    <div className="relative">
      {/* Bell Icon */}
      <button
        onClick={() => setShowPanel(!showPanel)}
        className={`relative p-2.5 hover:bg-gray-100 rounded-lg transition ${animate ? 'animate-bounce' : ''}`}
        data-testid="notification-bell"
        title="Notifications"
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
      >
        <BellRing className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse"
            data-testid="notification-badge-count"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {!isConnected && (
          <span className="absolute bottom-1 right-1 w-2 h-2 bg-amber-400 rounded-full" title="Reconnecting…" />
        )}
        {isConnected && (
          <span className="absolute bottom-1 right-1 w-2 h-2 bg-emerald-400 rounded-full" title="Live" />
        )}
      </button>

      {/* Notification Panel */}
      {showPanel && (
        <div
          className="absolute right-0 mt-2 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 overflow-hidden"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
            <div>
              <h3 className="font-bold text-lg">Notifications</h3>
              {personal && <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide bg-white/20 rounded-full px-2 py-0.5" data-testid="notification-personal-badge">Your alerts</span>}
              <p className="text-sm text-slate-300 flex items-center gap-1.5">
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide ml-1 ${isConnected ? 'text-emerald-300' : 'text-amber-300'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
                  {isConnected ? 'Live' : 'Offline'}
                </span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { fetchHistory(); }}
                className="p-2 hover:bg-white/10 rounded-lg transition"
                title="Refresh"
                data-testid="notification-refresh-btn"
                aria-label="Refresh notifications"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`p-2 hover:bg-white/10 rounded-lg transition ${showSettings ? 'bg-white/20' : ''}`}
                title="Notification Settings"
                data-testid="notification-settings-btn"
              >
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={markAllRead}
                className="text-sm text-slate-300 hover:text-white transition px-3 py-1 bg-white/10 rounded-lg"
                data-testid="mark-all-read"
              >
                Mark all read
              </button>
              <button
                onClick={() => setShowPanel(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition"
                aria-label="Close notifications"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="px-5 py-4 bg-slate-50 border-b border-slate-200">
              <h4 className="font-semibold text-slate-800 mb-3">Notification Settings</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {notificationSettings.soundEnabled ? (
                      <Volume2 className="w-4 h-4 text-slate-600" />
                    ) : (
                      <VolumeX className="w-4 h-4 text-slate-400" />
                    )}
                    <span className="text-sm text-slate-700">Sound Alerts</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationSettings.soundEnabled}
                      onChange={(e) => updateNotificationSettings({ soundEnabled: e.target.checked })}
                      className="sr-only peer"
                      data-testid="toggle-sound"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
                  </label>
                </div>

                {notificationSettings.soundEnabled && (
                  <div className="ml-6 space-y-2">
                    <p className="text-xs text-slate-500 mb-2">Sound Style</p>
                    <div className="flex gap-2">
                      {['chime', 'alert'].map(style => (
                        <button
                          key={style}
                          onClick={() => updateNotificationSettings({ soundType: style })}
                          className={`px-3 py-1.5 text-xs rounded-lg font-medium transition ${
                            notificationSettings.soundType === style
                              ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-500'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {style === 'chime' ? 'Subtle Chime' : 'Alert Tone'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center gap-2">
                    <BellRing className="w-4 h-4 text-slate-600" />
                    <span className="text-sm text-slate-700">Browser Push</span>
                  </div>
                  {getNotificationPermission() === 'granted' ? (
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationSettings.pushEnabled}
                        onChange={(e) => updateNotificationSettings({ pushEnabled: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
                    </label>
                  ) : getNotificationPermission() === 'denied' ? (
                    <span className="text-xs text-red-500">Blocked in browser</span>
                  ) : (
                    <button
                      onClick={handleRequestPushPermission}
                      className="px-3 py-1 text-xs bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition"
                    >
                      Enable
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="max-h-[440px] overflow-y-auto" data-testid="notification-list">
            {visibleItems.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No notifications yet</p>
                <p className="text-sm text-slate-400">We'll notify you when something happens</p>
              </div>
            ) : (
              visibleItems.map((notification) => {
                const cfg = getConfig(notification.type);
                const cc = colorClasses[cfg.color] || colorClasses.slate;
                const Icon = cfg.icon;
                const isHighPriority = notification.priority === 'high';
                return (
                  <div
                    key={notification._id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleItemClick(notification)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleItemClick(notification); }}
                    className={`w-full text-left px-5 py-4 border-b border-slate-100 hover:bg-slate-50 transition cursor-pointer ${
                      !notification.read ? `${cc.tint} border-l-4 ${cc.border}` : ''
                    } ${notification._live ? 'animate-pulse-once' : ''}`}
                    data-testid="notification-item"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-xl ${cc.bg} flex-shrink-0`}>
                        <Icon className={`w-5 h-5 ${cc.icon}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold text-slate-900 ${!notification.read ? '' : 'opacity-80'}`}>
                          {notification.title || cfg.fallbackTitle}
                        </p>
                        <p className="text-sm text-slate-600 mt-0.5 line-clamp-2 break-words">
                          {notification.message || notification.data?.message || ''}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {formatTimeAgo(notification.created_at)}
                        </p>
                        {notification.type === 'training_overdue_escalation' && notification.data?.assignment_id && (
                          <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                            <React.Suspense fallback={null}>
                              <NudgeButton assignmentId={notification.data.assignment_id} />
                            </React.Suspense>
                          </div>
                        )}
                      </div>
                      {!notification.read && (
                        <span className={`px-2 py-0.5 ${cc.bg} ${cc.icon} text-[10px] font-bold rounded-full uppercase tracking-wide flex-shrink-0`}>
                          {isHighPriority ? 'Urgent' : 'New'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
